// app/frontend/components/ui/Navigation/UserSection.tsx
import { Menu, Group, UnstyledButton, Box } from "@mantine/core";
import {
  IconUser,
  IconSettings,
  IconLogout,
  IconChevronDown,
  IconLayoutDashboard,
  IconHeart,
  IconBookmark,
} from "@tabler/icons-react";
import { router } from "@inertiajs/react";
import UserAvatar from "./UserAvatar";

interface User {
  id: number;
  email: string;
  name: string;
  admin: boolean;
  avatar_url?: string | null;
}

interface UserSectionProps {
  user: User | null;
}

/**
 * UserSection - User avatar with dropdown menu for navigation
 */
export function UserSection({ user }: UserSectionProps) {
  if (!user) {
    return null;
  }

  const handleLogout = () => {
    // Use Inertia router for logout with DELETE method
    router.delete("/logout", {
      preserveScroll: true,
    });
  };

  const handleNavigation = (path: string) => {
    router.visit(path);
  };

  return (
    <Menu
      width={220}
      position="bottom-end"
      shadow="lg"
      styles={{
        dropdown: {
          background: "rgba(23, 23, 23, 0.98)",
          border: "1px solid rgba(255, 152, 0, 0.2)",
          backdropFilter: "blur(15px)",
        },
        item: {
          color: "#E0E0E0",
          "&[data-hovered]": {
            background: "rgba(255, 152, 0, 0.1)",
            color: "#FF9800",
          },
        },
        divider: {
          borderColor: "rgba(255, 255, 255, 0.1)",
        },
      }}
    >
      <Menu.Target>
        <UnstyledButton
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 152, 0, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <Group gap="sm">
            {/* Mobile: Avatar only */}
            <Box hiddenFrom="sm">
              <UserAvatar user={user} size="sm" />
            </Box>
            {/* Desktop: Avatar with name and email */}
            <Box visibleFrom="sm">
              <UserAvatar user={user} size="sm" showName showEmail />
            </Box>
            <IconChevronDown size={14} color="#888" />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label style={{ color: "#888" }}>Account</Menu.Label>
        <Menu.Item
          leftSection={<IconLayoutDashboard size={16} />}
          onClick={() => handleNavigation("/dashboard")}
        >
          Dashboard
        </Menu.Item>
        <Menu.Item
          leftSection={<IconHeart size={16} />}
          onClick={() => handleNavigation("/videos/favorites")}
        >
          Favorites
        </Menu.Item>
        <Menu.Item
          leftSection={<IconBookmark size={16} />}
          onClick={() => handleNavigation("/videos/bookmarks")}
        >
          Bookmarks
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item
          leftSection={<IconUser size={16} />}
          onClick={() => handleNavigation("/profile")}
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconSettings size={16} />}
          onClick={() => handleNavigation("/settings")}
        >
          Settings
        </Menu.Item>

        {user.admin && (
          <>
            <Menu.Divider />
            <Menu.Label style={{ color: "#888" }}>Admin</Menu.Label>
            <Menu.Item
              leftSection={<IconSettings size={16} />}
              onClick={() => handleNavigation("/admin")}
              style={{ color: "#FF9800" }}
            >
              Admin Panel
            </Menu.Item>
          </>
        )}

        <Menu.Divider />

        <Menu.Item
          leftSection={<IconLogout size={16} />}
          onClick={handleLogout}
          color="red"
        >
          Log out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export default UserSection;
